import { smartLink } from '@/lib/deep-link';
import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Bell, CheckCheck, MessageSquare, AtSign } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/ui/Card';
import {
  fetchNotifications, markNotificationRead, markAllNotificationsRead,
  type Notification,
} from '@/lib/queries';
import { cn } from '@/lib/cn';

export default function NotificationsPage() {
  const qc = useQueryClient();
  // Everything lands in one stream, so the messages that name you personally get lost
  // among the ones that merely happened. This is the split Farouk asked for.
  const [mine, setMine] = useState(false);
  const list = useQuery({
    queryKey: ['notifications', mine],
    queryFn: () => fetchNotifications({ mine_only: mine }),
    refetchInterval: 30_000,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['unread-count'] });
    },
  });

  const markAll = useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: () => {
      toast.success('All read');
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['unread-count'] });
    },
  });

  const unreadCount = (list.data ?? []).filter(n => !n.read).length;

  return (
    <div>
      <PageHeader
        title="Notifications"
        subtitle={unreadCount > 0 ? `${unreadCount} unread` : 'Caught up.'}
        actions={unreadCount > 0 ? (
          <button
            onClick={() => markAll.mutate()}
            disabled={markAll.isPending}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-bg-card hover:bg-slate-800 text-muted-strong border border-slate-800"
          >
            <CheckCheck size={12} /> Mark all read
          </button>
        ) : undefined}
      />

      <div className="flex gap-2 mb-4" data-testid="notif-tabs">
        {([[false, 'Everything'], [true, 'For you']] as const).map(([v, label]) => (
          <button key={label} onClick={() => setMine(v)} data-testid={v ? 'notif-mine' : 'notif-all'}
            className={cn('text-xs px-3 py-1.5 min-h-[36px] rounded-full border inline-flex items-center gap-1.5 transition',
              mine === v ? 'border-accent-400 bg-accent-600/20 text-accent-400' : 'border-slate-800 text-muted hover:text-muted-strong')}>
            {v && <AtSign size={12} />}{label}
          </button>
        ))}
      </div>

      {list.isLoading && <div className="text-muted text-sm">Loading...</div>}
      {!list.isLoading && (list.data ?? []).length === 0 && (
        <div className="rounded-xl border border-slate-800 bg-bg-card/40 py-16 text-center text-muted text-sm">
          <Bell size={28} className="mx-auto mb-2 text-muted/50" />
          No notifications yet.
        </div>
      )}

      <div className="space-y-2">
        {(list.data ?? []).map(n => (
          <NotificationRow key={n.id} n={n} onRead={() => !n.read && markRead.mutate(n.id)} />
        ))}
      </div>
    </div>
  );
}

function NotificationRow({ n, onRead }: { n: Notification; onRead: () => void }) {
  const isReview = n.kind === 'review_requested' || n.kind === 'review_replied';
  const inner = (
    <>
      <div className="shrink-0 mt-0.5">
        {isReview ? (
          <div className="w-8 h-8 rounded-full bg-accent-600/20 text-accent-400 flex items-center justify-center">
            <MessageSquare size={14} />
          </div>
        ) : (
          <div className="w-8 h-8 rounded-full bg-slate-700/40 text-muted-strong flex items-center justify-center">
            <Bell size={14} />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-muted-strong">{n.title}</div>
        {/* the title carries the code, which nobody recognises; the name goes here */}
        {n.creative_title && (
          <div className="text-xs text-accent-300/90 truncate mt-0.5" data-testid="notif-piece">{n.creative_title}</div>
        )}
        {n.body && <div className="text-xs text-muted line-clamp-2 mt-0.5">{n.body}</div>}
        <div className="text-[10px] text-muted/60 mt-1">
          {n.actor_name && `${n.actor_name} · `}{relTime(n.created_at)}
        </div>
      </div>
      {!n.read && <div className="w-2 h-2 rounded-full bg-accent-400 shrink-0 mt-2" />}
    </>
  );

  const className = cn(
    'flex items-start gap-3 p-3 rounded-xl border transition',
    n.read ? 'border-slate-800 bg-bg-card/30' : 'border-accent-400/30 bg-accent-600/5 hover:bg-accent-600/10',
  );

  // the exact place the notification is about (lib/deep-link)
  const to = smartLink(n);
  if (to) {
    return (
      <Link to={to} onClick={onRead} className={className}>
        {inner}
      </Link>
    );
  }
  return (
    <button onClick={onRead} className={cn(className, 'w-full text-left')}>
      {inner}
    </button>
  );
}

function relTime(iso: string): string {
  const sec = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60)    return `${sec}s ago`;
  if (sec < 3600)  return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}
